/**
 * L'inventaire d'un magasin : ouvrir, imprimer la feuille, compter, valider.
 *
 * *Demande de Chris, 15/09/2026 : « je pense qu'il manque un menu pour saisir l'inventaire et pour
 * éditer une feuille d'inventaire. »* Et, tranché le même jour : **la feuille est aveugle**.
 *
 * ## Deux écrans dans un seul, et c'est le déroulé du travail
 *
 * La liste des inventaires, et le détail de celui qu'on compte. On n'y navigue pas vraiment : on
 * ouvre un inventaire, on imprime, on compte, on revient saisir, on valide. Un second écran aurait
 * ajouté un aller-retour à un travail qui en compte déjà un — le rayonnage.
 *
 * ## Ce que l'écran montre, et pourquoi
 *
 * **Le théorique est visible À LA SAISIE**, et c'est volontaire : la feuille imprimée, elle, ne le
 * porte pas. On compte sur le papier sans rien savoir, puis on saisit devant l'écart — c'est là
 * qu'il faut le voir, pour attraper une erreur de comptage avant qu'elle ne devienne un mouvement
 * de stock.
 *
 * **Une ligne non comptée se distingue d'un comptage à zéro.** La première ne régularisera rien,
 * la seconde videra l'article. Elles sont affichées différemment, et le compteur de lignes
 * restantes est répété au-dessus du bouton de validation : valider en laissant des lignes de côté
 * est permis — un inventaire se fait rarement d'un seul tenant — mais jamais par inadvertance.
 *
 * **Les lignes sont dans l'ordre du magasin**, l'emplacement d'abord : c'est l'ordre de la feuille
 * imprimée, donc l'ordre dans lequel on saisit.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  abandonnerUnInventaire,
  adresseFeuilleDInventaire,
  ajouterUneLigneDInventaire,
  chargerInventaire,
  chargerInventaires,
  chercherArticles,
  compterUneLigne,
  ouvrirUnInventaire,
  validerUnInventaire,
} from "./api";
import type { Inventaire, InventaireEnTete, SiteDeStockage } from "./api";
import { codeDuScan, lireQrDansImage } from "./scan";

/** Une date civile « 2026-09-15 » se lit « 15/09/2026 », sans passer par un objet date. */
function formaterDateCivile(iso: string): string {
  const [a, m, j] = iso.split("-");
  return `${j ?? ""}/${m ?? ""}/${a ?? ""}`;
}

/** Un décimal sans les zéros inutiles : « 12.000 » se lit « 12 ». */
function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

/** Aujourd'hui en « AAAA-MM-JJ », lu sur l'horloge locale et jamais par un fuseau. */
function aujourdhui(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Inventaires(): JSX.Element {
  const [liste, setListe] = useState<readonly InventaireEnTete[]>([]);
  const [sites, setSites] = useState<readonly SiteDeStockage[]>([]);
  const [ouvert, setOuvert] = useState<Inventaire | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  // L'ouverture d'un inventaire neuf.
  const [site, setSite] = useState("");
  const [compteLe, setCompteLe] = useState(aujourdhui());
  const [note, setNote] = useState("");

  /**
   * Atteindre une ligne : par scan de l'étiquette, ou par son code.
   *
   * *Demande de Chris, 15/09/2026 : « lorsque l'on saisie un inventaire il faudrait pouvoir
   * scanner le qr code ou saisir le code ».*
   *
   * **Un seul champ pour les deux gestes, et pour les deux issues.** Chercher une ligne dans une
   * feuille de deux cents références et ajouter un article qu'on trouve sans l'attendre sont la
   * même question posée au magasin — « voilà ce que j'ai dans les mains » —, et la réponse ne
   * dépend que de ce que la feuille contient déjà. Deux champs auraient obligé à savoir, avant de
   * scanner, si l'article était prévu ou non : exactement ce qu'on ne sait pas devant un rayonnage.
   */
  const [cherche, setCherche] = useState("");
  const [messageScan, setMessageScan] = useState<string | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);
  /**
   * La ligne qu'on vient d'atteindre, mise en avant le temps de la saisir.
   *
   * Sans elle, scanner une étiquette sur une feuille de deux cents lignes ne montrerait rien : le
   * champ prendrait le focus quelque part hors de l'écran, et le magasinier croirait que le scan
   * n'a pas marché.
   */
  const [ligneEnAvant, setLigneEnAvant] = useState<string | null>(null);
  /** Les champs de comptage, par ligne : c'est par eux qu'on amène la bonne à l'écran. */
  const champsDeComptage = useRef(new Map<string, HTMLInputElement>());

  const rechargerLaListe = useCallback((): void => {
    chargerInventaires()
      .then((r) => {
        setListe(r.inventaires);
        setSites(r.sites);
        if (r.sites.length === 1) setSite((actuel) => (actuel === "" ? (r.sites[0]?.id ?? "") : actuel));
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, []);

  useEffect(() => {
    rechargerLaListe();
  }, [rechargerLaListe]);

  /**
   * Amène une ligne sous les yeux et lui donne le focus.
   *
   * Le défilement est **doux et centré** : un saut sec laisse le doute sur ce qui a bougé, alors
   * qu'on veut voir la ligne arriver. Le focus met le curseur dans le champ de comptage — le geste
   * suivant est toujours de taper un nombre.
   */
  const atteindre = useCallback((ligneId: string): void => {
    setLigneEnAvant(ligneId);
    // Après le rendu : le champ n'existe pas encore quand la ligne vient d'être ajoutée.
    window.setTimeout(() => {
      const champ = champsDeComptage.current.get(ligneId);
      if (champ === undefined) return;
      // `inline: "nearest"` borne le défilement HORIZONTAL au strict nécessaire : sans lui, le
      // cadre du tableau se recentre sur le champ et emporte le code hors de l'écran.
      champ.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      champ.focus();
      champ.select();
    }, 60);
  }, []);

  /**
   * Le code d'un article : on l'atteint sur la feuille, ou on l'y ajoute.
   *
   * Les trois issues sont nommées, et aucune ne se tait. Un article **déjà sur la feuille** est
   * simplement atteint, même s'il est déjà compté : on corrige un comptage de la même façon qu'on
   * le pose. Un article **du référentiel mais absent de la feuille** s'ajoute — c'est ce qu'on
   * trouve sans l'attendre, et le serveur lui donne le stock qu'il a dans ce magasin, jamais zéro.
   * Un code **inconnu du référentiel** le dit : c'est probablement une étiquette d'un autre
   * produit, ou un article qui reste à créer.
   */
  const surCode = useCallback(
    (code: string): void => {
      if (ouvert === null) return;
      const propre = code.trim();
      if (propre === "") return;
      setMessageScan(null);

      const deja = ouvert.lignes.find((l) => l.code === propre);
      if (deja !== undefined) {
        setCherche("");
        /*
          On NOMME la ligne atteinte, et pas seulement on la met en avant.

          Vu à 390 px le 15/09/2026 : le tableau défile de côté pour amener le champ de comptage
          sous les yeux, et le code sort de l'écran au passage — le magasinier tape alors un nombre
          sans plus voir sur quoi. Le message reste au-dessus du tableau, immobile, et dit ce qu'on
          est en train de compter.
        */
        setMessageScan(`${deja.code} — ${deja.description} : saisissez le comptage.`);
        atteindre(deja.id);
        return;
      }

      chercherArticles(propre)
        .then((r) => {
          const exact = r.articles.find((a) => a.code === propre);
          if (exact === undefined) {
            setMessageScan(`Aucun article ne porte le code « ${propre} ».`);
            return;
          }
          return ajouterUneLigneDInventaire(ouvert.id, exact.id).then((maj) => {
            setOuvert(maj.inventaire);
            setCherche("");
            const ajoutee = maj.inventaire.lignes.find((l) => l.articleId === exact.id);
            setMessageScan(
              `${exact.code} — ${exact.description} : ajouté à la feuille, saisissez le comptage.`,
            );
            if (ajoutee !== undefined) atteindre(ajoutee.id);
          });
        })
        .catch((x: unknown) => {
          setMessageScan(x instanceof Error ? x.message : String(x));
        });
    },
    [ouvert, atteindre],
  );

  /** Le scan d'une étiquette : même décodeur que l'écran de saisie (`scan.ts`). */
  const scanner = (fichier: File): void => {
    setMessageScan("Lecture de l'étiquette…");
    lireQrDansImage(fichier)
      .then((scan) => {
        const code = scan === null ? null : codeDuScan(scan);
        if (code === null) {
          setMessageScan(
            "Étiquette illisible. Reprendre la photo de plus près, bien en face et sans reflet.",
          );
          return;
        }
        setMessageScan(null);
        surCode(code);
      })
      .catch((x: unknown) => {
        setMessageScan(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        // Sans cette remise à zéro, scanner DEUX fois la même étiquette ne déclenche rien :
        // l'input ne signale pas de changement si le fichier choisi est identique.
        if (champFichier.current !== null) champFichier.current.value = "";
      });
  };

  const ouvrirLeDetail = (id: string): void => {
    setErreur(null);
    chargerInventaire(id)
      .then((r) => {
        setOuvert(r.inventaire);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  };

  const ouvrir = (): void => {
    setOccupe(true);
    setErreur(null);
    setAvis(null);
    ouvrirUnInventaire({ siteId: site, compteLe, note: note.trim() === "" ? null : note.trim() })
      .then((r) => {
        setOuvert(r.inventaire);
        setNote("");
        setAvis(
          `Inventaire ouvert : ${String(r.inventaire.compte.lignes)} référence` +
            `${r.inventaire.compte.lignes > 1 ? "s" : ""} à compter. Imprimez la feuille.`,
        );
        rechargerLaListe();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  /**
   * Enregistre un comptage à la sortie du champ.
   *
   * À la sortie et non à la frappe : chaque frappe enverrait une écriture, et « 82 » passerait par
   * « 8 » — donc par un écart de 74 affiché une fraction de seconde, ce qui est exactement le
   * genre de nombre qu'on lit de travers en saisissant vite.
   */
  const compter = (ligneId: string, valeur: string, remarque: string | null): void => {
    if (ouvert === null) return;
    compterUneLigne(ouvert.id, ligneId, {
      quantiteComptee: valeur.trim() === "" ? null : valeur.trim(),
      remarque,
    })
      .then((r) => {
        setOuvert(r.inventaire);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  };

  const valider = (): void => {
    if (ouvert === null) return;
    setOccupe(true);
    setErreur(null);
    validerUnInventaire(ouvert.id)
      .then((r) => {
        setOuvert(r.inventaire);
        /*
          Les trois nombres qui comptent, et aucun ne se tait.

          Ce qui a été régularisé, et ce qui ne l'a PAS été faute d'avoir été compté : une ligne
          laissée de côté ne change rien au stock, et le croire corrigé serait pire que de ne rien
          faire.
        */
        setAvis(
          `Inventaire validé. ${String(r.regularisations)} régularisation` +
            `${r.regularisations > 1 ? "s" : ""} posée${r.regularisations > 1 ? "s" : ""} en stock.` +
            (r.nonComptees > 0
              ? ` ${String(r.nonComptees)} ligne${r.nonComptees > 1 ? "s" : ""} non comptée` +
                `${r.nonComptees > 1 ? "s" : ""} : leur stock est inchangé.`
              : ""),
        );
        rechargerLaListe();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  const abandonner = (): void => {
    if (ouvert === null) return;
    setOccupe(true);
    abandonnerUnInventaire(ouvert.id)
      .then(() => {
        setOuvert(null);
        setAvis("Inventaire abandonné. Rien n'a été régularisé.");
        rechargerLaListe();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  return (
    <section className="inventaires">
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      {avis !== null && <p className="avis">{avis}</p>}

      {ouvert === null ? (
        <>
          <h3>Ouvrir un inventaire</h3>
          {sites.length === 0 ? (
            <p className="alerte" role="alert">
              Aucun site de stockage n&apos;est déclaré. Cochez « site de stockage » sur au moins
              un site dans Référentiels → Carrières et sites du groupe.
            </p>
          ) : (
            <form
              className="inventaires__ouverture"
              onSubmit={(e) => {
                e.preventDefault();
                ouvrir();
              }}
            >
              {/* Masqué quand il n'y a qu'un magasin : il est présélectionné. */}
              {sites.length > 1 && (
                <label>
                  Magasin à compter
                  <select
                    value={site}
                    onChange={(e) => {
                      setSite(e.target.value);
                    }}
                  >
                    <option value="">— choisir —</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.denomination}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label>
                Date du comptage
                <input
                  type="date"
                  value={compteLe}
                  onChange={(e) => {
                    setCompteLe(e.target.value);
                  }}
                />
                {/* On compte le vendredi, on saisit le lundi : c'est la date du comptage qui
                    datera les mouvements de régularisation, jamais celle de la saisie. */}
                <small>Le jour où l&apos;on compte, pas celui où l&apos;on saisit.</small>
              </label>

              <label>
                Note
                <input
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                />
              </label>

              <button type="submit" disabled={occupe || site === ""}>
                {occupe ? "Ouverture…" : "Ouvrir l'inventaire"}
              </button>
            </form>
          )}

          <h3>Inventaires</h3>
          {liste.length === 0 ? (
            <p className="etat">Aucun inventaire pour l&apos;instant.</p>
          ) : (
            <table className="inventaires__liste">
              <thead>
                <tr>
                  <th>Comptage</th>
                  <th>Magasin</th>
                  <th>État</th>
                  <th>Ouvert par</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.map((i) => (
                  <tr key={i.id}>
                    <td>{formaterDateCivile(i.compteLe)}</td>
                    <td>{i.siteDenomination}</td>
                    <td>
                      {i.statut === "ouvert" ? (
                        <strong>En cours</strong>
                      ) : (
                        `Validé${i.valideParNom === null ? "" : ` par ${i.valideParNom}`}`
                      )}
                    </td>
                    <td>{i.ouvertParNom ?? "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="lien"
                        onClick={() => {
                          ouvrirLeDetail(i.id);
                        }}
                      >
                        {i.statut === "ouvert" ? "Compter" : "Consulter"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <>
          <div className="inventaires__entete">
            <h3>
              {ouvert.siteDenomination} — comptage du {formaterDateCivile(ouvert.compteLe)}
            </h3>
            <button
              type="button"
              className="lien"
              onClick={() => {
                setOuvert(null);
                setAvis(null);
              }}
            >
              Retour à la liste
            </button>
          </div>

          <p className="inventaires__compte">
            {ouvert.compte.comptees} / {ouvert.compte.lignes} comptées
            {ouvert.compte.restantes > 0 && ` · ${String(ouvert.compte.restantes)} restantes`}
            {ouvert.compte.ecarts > 0 && (
              <>
                {" · "}
                <strong>
                  {ouvert.compte.ecarts} écart{ouvert.compte.ecarts > 1 ? "s" : ""}
                </strong>
              </>
            )}
          </p>

          {ouvert.statut === "ouvert" && (
            <>
              <div className="inventaires__gestes">
                {/*
                  La feuille s'ouvre dans un onglet et s'imprime depuis le navigateur — comme les
                  planches d'étiquettes, aucun service de rendu PDF n'existant côté serveur.
                */}
                <a href={adresseFeuilleDInventaire(ouvert.id)} target="_blank" rel="noreferrer">
                  Éditer la feuille d&apos;inventaire
                </a>
                <button type="button" className="lien" onClick={abandonner} disabled={occupe}>
                  Abandonner cet inventaire
                </button>
              </div>

              {/*
                SCANNER OU SAISIR LE CODE (demande de Chris, 15/09/2026).

                En TÊTE de la feuille et non sous le tableau : c'est par là qu'on entre quand on
                compte, une ligne après l'autre, l'étiquette à la main. La liste en dessous sert à
                relire et à corriger.

                Le même décodeur que l'écran de saisie, et le geste s'achève de la même façon —
                l'appareil photo arrière, une seule tentative, un message quand l'étiquette est
                illisible. Un magasinier n'a pas à apprendre deux scans.
              */}
              <div className="inventaires__atteindre">
                <label className="stocks__scan">
                  <span>Scanner une étiquette</span>
                  <input
                    ref={champFichier}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const fichier = e.target.files?.[0];
                      if (fichier !== undefined) scanner(fichier);
                    }}
                  />
                </label>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    surCode(cherche);
                  }}
                >
                  <label>
                    ou saisir le code
                    <input
                      value={cherche}
                      placeholder="1000110"
                      // `decimal` et non `number` : les codes du référentiel sont des suites de
                      // chiffres, et le pavé numérique évite d'ouvrir le clavier entier sur un
                      // téléphone qu'on tient d'une main.
                      inputMode="decimal"
                      onChange={(e) => {
                        setCherche(e.target.value);
                      }}
                    />
                    <small>
                      Sur la feuille, la ligne s&apos;ouvre. Absent, l&apos;article s&apos;ajoute.
                    </small>
                  </label>
                </form>
              </div>
              {messageScan !== null && <p className="etat">{messageScan}</p>}
            </>
          )}

          {/*
            Le tableau defile de cote DANS SON CADRE, et la page ne bouge pas.

            Vu a 390 px le 15/09/2026 : sept colonnes ne tiennent pas sur un telephone, et sans ce
            cadre c est la page entiere qui partait de cote — le bouton « Valider » se retrouvant
            hors de l ecran des qu on avait fait defiler pour lire une remarque.
          */}
          <div className="inventaires__defilement">
          <table className="inventaires__lignes">
            <thead>
              <tr>
                <th>Emplacement</th>
                <th>Code</th>
                <th>Libellé</th>
                <th>Théorique</th>
                <th>Compté</th>
                <th>Écart</th>
                <th>Remarque</th>
              </tr>
            </thead>
            <tbody>
              {ouvert.lignes.map((l) => {
                const ecart = l.ecart === null ? null : Number(l.ecart);
                return (
                  <tr
                    key={l.id}
                    className={[
                      l.quantiteComptee === null
                        ? "inventaires__a-compter"
                        : ecart !== null && ecart !== 0
                          ? "inventaires__ecart"
                          : "",
                      // La ligne qu'on vient d'atteindre : sans repère, le scan semblerait
                      // n'avoir rien fait sur une feuille de deux cents lignes.
                      l.id === ligneEnAvant ? "inventaires__atteinte" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <td>{l.emplacement ?? "—"}</td>
                    <td className="inventaires__code">{l.code}</td>
                    <td>{l.description}</td>
                    {/* Visible ICI et pas sur la feuille : on compte à l'aveugle, on saisit
                        devant l'écart. */}
                    <td>
                      {sansZeros(l.quantiteTheorique)} {l.unite ?? ""}
                    </td>
                    <td>
                      {ouvert.statut === "ouvert" ? (
                        <input
                          inputMode="decimal"
                          // `key` sur la valeur comptée : sans elle, React garde le champ tel quel
                          // quand le serveur renvoie l'inventaire, et vider un comptage à l'écran
                          // ne remettrait pas le champ à blanc.
                          key={`${l.id}-${l.quantiteComptee ?? ""}`}
                          defaultValue={l.quantiteComptee === null ? "" : sansZeros(l.quantiteComptee)}
                          aria-label={`Compté pour ${l.code}`}
                          ref={(el) => {
                            if (el === null) champsDeComptage.current.delete(l.id);
                            else champsDeComptage.current.set(l.id, el);
                          }}
                          onBlur={(e) => {
                            compter(l.id, e.target.value, l.remarque);
                          }}
                        />
                      ) : l.quantiteComptee === null ? (
                        "non compté"
                      ) : (
                        sansZeros(l.quantiteComptee)
                      )}
                    </td>
                    <td className={ecart !== null && ecart !== 0 ? "alerte" : undefined}>
                      {/* « Pas encore compté » n'est PAS un écart de zéro : le dire autrement
                          ferait lire « tout est juste » sur une ligne que personne n'a vue. */}
                      {l.ecart === null ? "—" : `${ecart !== null && ecart > 0 ? "+" : ""}${sansZeros(l.ecart)}`}
                    </td>
                    <td>
                      {ouvert.statut === "ouvert" ? (
                        <input
                          defaultValue={l.remarque ?? ""}
                          aria-label={`Remarque pour ${l.code}`}
                          onBlur={(e) => {
                            compter(
                              l.id,
                              l.quantiteComptee === null ? "" : l.quantiteComptee,
                              e.target.value.trim() === "" ? null : e.target.value.trim(),
                            );
                          }}
                        />
                      ) : (
                        (l.remarque ?? "")
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {ouvert.statut === "ouvert" && (
            <>
              {/*
                Valider en laissant des lignes non comptées est PERMIS et annoncé.

                Un inventaire se fait rarement d'un seul tenant, et bloquer obligerait à saisir des
                zéros sur ce qu'on n'a pas vu — ce qui viderait le stock. Mais le nombre est répété
                juste au-dessus du bouton : permis ne veut pas dire par inadvertance.
              */}
              {ouvert.compte.restantes > 0 && (
                <p className="etat">
                  {ouvert.compte.restantes} ligne{ouvert.compte.restantes > 1 ? "s" : ""} non
                  comptée{ouvert.compte.restantes > 1 ? "s" : ""} : leur stock restera inchangé.
                </p>
              )}
              <button type="button" onClick={valider} disabled={occupe}>
                {occupe
                  ? "Validation…"
                  : `Valider et régulariser ${String(ouvert.compte.ecarts)} écart${ouvert.compte.ecarts > 1 ? "s" : ""}`}
              </button>
            </>
          )}
        </>
      )}
    </section>
  );
}
